import React, { useState } from 'react';
import CollapsiblePanel from './CollapsiblePanel';
import ToolsPicker from './ToolsPicker';
import CharacterPicker from './CharacterPicker';
import ParagraphPicker from './ParagraphPicker';
import StrokePicker from './StrokePicker';
import ColorPicker from './ColorPicker';
import LayersPicker from './LayersPicker';

const ToolsSidebar = ({ 
  allPanelsOpen, 
  toggleAllPanels, 
  fillColor, 
  fillOpacity, 
  strokeColor, 
  strokeWidth, 
  strokeOpacity, 
  strokeStyle, 
  onColorChange, 
  onStrokeChange 
}) => {
  const [openPanels, setOpenPanels] = useState({
    tools: true,
    character: false,
    paragraph: false,
    stroke: false,
    color: false,
    layers: true
  });

  const [canvasSize, setCanvasSize] = useState({
    width: 1080,
    height: 1080
  });
  
  const [colorState, setColorState] = useState({
    color: '#FFFFFF',
    opacity: 1.0
  });

  const [strokeState, setStrokeState] = useState({
    weight: 2,
    style: 'solid',
    color: '#000000',
    opacity: 0.64
  });

  const [characterState, setCharacterState] = useState({
    fontSize: 16,
    fontFamily: 'Arial',
    fontWeight: '400',
    textColor: '#000000'
  });

  const [paragraphState, setParagraphState] = useState({
    textAlign: 'left',
    lineHeight: 1.2,
    letterSpacing: 0,
    paragraphSpacing: 0
  });

  const [layersState, setLayersState] = useState([
    {
      id: 'group1',
      name: 'Group 1',
      type: 'group',
      visible: true,
      locked: false,
      children: [
        { id: 'text1', name: 'Welcome Text', type: 'text', visible: true, locked: false },
        { id: 'bg1', name: 'Background', type: 'rectangle', visible: true, locked: false }
      ]
    },
    { id: 'circle1', name: 'Circle Shape', type: 'circle', visible: true, locked: false },
    { id: 'hero1', name: 'Hero Image', type: 'image', visible: false, locked: false },
    { id: 'logo1', name: 'Logo Text', type: 'text', visible: true, locked: true }
  ]);

  // History management for undo functionality
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Sync panel states with allPanelsOpen prop
  React.useEffect(() => {
    setOpenPanels({
      tools: allPanelsOpen,
      character: allPanelsOpen,
      paragraph: allPanelsOpen,
      stroke: allPanelsOpen,
      color: allPanelsOpen,
      layers: allPanelsOpen
    });
  }, [allPanelsOpen]);

  // Initialize history with current state
  React.useEffect(() => {
    if (history.length === 0) {
      const initialState = {
        layers: layersState,
        color: colorState,
        stroke: strokeState,
        character: characterState,
        paragraph: paragraphState,
        timestamp: Date.now()
      };
      setHistory([initialState]);
      setHistoryIndex(0);
    }
  }, []);

  // Add to history when states change
  const addToHistory = (newLayers, newColor, newStroke, newCharacter, newParagraph) => {
    const newState = {
      layers: JSON.parse(JSON.stringify(newLayers || layersState)),
      color: { ...(newColor || colorState) },
      stroke: { ...(newStroke || strokeState) },
      character: { ...(newCharacter || characterState) },
      paragraph: { ...(newParagraph || paragraphState) },
      timestamp: Date.now()
    };

    // Remove any future history if we're not at the end
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newState);

    // Limit history to 50 states
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex(prev => prev + 1);
    }

    setHistory(newHistory);
  };

  // Undo function
  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      
      setLayersState(previousState.layers);
      setColorState(previousState.color);
      setStrokeState(previousState.stroke);
      setCharacterState(previousState.character);
      setParagraphState(previousState.paragraph);
      setHistoryIndex(newIndex);
      
      console.log('Undid to state:', previousState.timestamp);
    }
  };

  // Redo function
  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      
      setLayersState(nextState.layers);
      setColorState(nextState.color);
      setStrokeState(nextState.stroke);
      setCharacterState(nextState.character);
      setParagraphState(nextState.paragraph);
      setHistoryIndex(newIndex);
      
      console.log('Redid to state:', nextState.timestamp);
    }
  };

  // Keyboard shortcut handler
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          undo();
        } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
          e.preventDefault();
          redo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, history]);

  const togglePanel = (panelName) => {
    setOpenPanels(prev => ({
      ...prev,
      [panelName]: !prev[panelName]
    }));
  };



  const handleColorChange = (newColorState) => {
    setColorState(newColorState);
    addToHistory(layersState, newColorState, strokeState, characterState, paragraphState);
    console.log('Color changed:', newColorState);
  };

  const handleStrokeChange = (newStrokeState) => {
    setStrokeState(newStrokeState);
    addToHistory(layersState, colorState, newStrokeState, characterState, paragraphState);
    console.log('Stroke changed:', newStrokeState);
  };

  const handleCharacterChange = (newCharacterState) => {
    setCharacterState(newCharacterState);
    addToHistory(layersState, colorState, strokeState, newCharacterState, paragraphState);
    console.log('Character changed:', newCharacterState);
  };

  const handleParagraphChange = (newParagraphState) => {
    setParagraphState(newParagraphState);
    addToHistory(layersState, colorState, strokeState, characterState, newParagraphState);
    console.log('Paragraph changed:', newParagraphState);
  };

  const handleLayersChange = (newLayers) => {
    setLayersState(newLayers);
    addToHistory(newLayers, colorState, strokeState, characterState, paragraphState);
    console.log('Layers changed:', newLayers);
  };

  const handleCanvasSizeChange = (newSize) => {
    setCanvasSize(newSize);
    console.log('Canvas size changed:', newSize);
  };

  return (
    <div className="bg-black tools-sidebar">
      <div className="tools-sidebar-content">
        <CollapsiblePanel
          title="Tools"
          isOpen={openPanels.tools}
          onToggle={() => togglePanel('tools')}
        >
          <ToolsPicker 
            canvasWidth={canvasSize.width}
            canvasHeight={canvasSize.height}
            onCanvasSizeChange={handleCanvasSizeChange}
          />
        </CollapsiblePanel>

        <CollapsiblePanel
          title="Character"
          isOpen={openPanels.character}
          onToggle={() => togglePanel('character')}
        >
          <CharacterPicker 
            fontSize={characterState.fontSize}
            fontFamily={characterState.fontFamily}
            fontWeight={characterState.fontWeight}
            textColor={characterState.textColor}
            onChange={handleCharacterChange}
          />
        </CollapsiblePanel>

        <CollapsiblePanel
          title="Paragraph"
          isOpen={openPanels.paragraph}
          onToggle={() => togglePanel('paragraph')}
        >
          <ParagraphPicker 
            textAlign={paragraphState.textAlign}
            lineHeight={paragraphState.lineHeight}
            letterSpacing={paragraphState.letterSpacing}
            paragraphSpacing={paragraphState.paragraphSpacing}
            onChange={handleParagraphChange}
          />
        </CollapsiblePanel>

        <CollapsiblePanel
          title="Stroke"
          isOpen={openPanels.stroke}
          onToggle={() => togglePanel('stroke')}
        >
          <StrokePicker 
            weight={strokeWidth}
            style={strokeStyle}
            color={strokeColor}
            opacity={strokeOpacity}
            onChange={onStrokeChange}
          />
        </CollapsiblePanel>

        <CollapsiblePanel
          title="Fill"
          isOpen={openPanels.color}
          onToggle={() => togglePanel('color')}
        >
          <ColorPicker 
            color={fillColor}
            opacity={fillOpacity}
            onChange={onColorChange}
          />
        </CollapsiblePanel>

        <CollapsiblePanel
          title="Layers"
          isOpen={openPanels.layers}
          onToggle={() => togglePanel('layers')}
        >
          <LayersPicker 
            layers={layersState}
            onChange={handleLayersChange}
          />
        </CollapsiblePanel>
      </div>
    </div>
  );
};

export default ToolsSidebar;
